import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { X, ScanLine, Flashlight, FlashlightOff, ChevronUp, ChevronDown } from 'lucide-react';
// See GLOSSARY.md for authoritative definitions of all scanner modes.
import { ScannerMode } from '../../../lib/contracts';
import { useCoreContextOptional } from '../../../lib/coreContext';
import useCameraStream from '../../../lib/hooks/useCameraStream';
import { BrowserMultiFormatReader } from '@zxing/browser';
import {
  compressAndResizeImage,
  getCooldownForMode,
  IMAGE_COMPRESSION_QUALITY,
  MAX_IMAGE_DIMENSION,
  MAX_UPLOAD_MB,
  validateUpc,
} from './scannerUtils';
import { useScannerFeedback } from './useScannerFeedback';
import {
  ScannerCameraIdleOverlay,
  ScannerErrorChip,
  ScannerFlashOverlay,
  ScannerLaserOverlay,
  ScannerManualUpcFallback,
} from './ScannerOverlays';
import { ScannerReceiptControls, ScannerReceiptHeader } from './ScannerReceiptControls';

interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
}

interface DetectedBarcode {
  rawValue: string;
  format: string;
}

interface BrowserBarcodeDetector {
  detect: (image: ImageBitmapSource) => Promise<DetectedBarcode[]>;
}

type BarcodeDetectorCtor = new (options?: { formats: string[] }) => BrowserBarcodeDetector;

type WindowWithBarcodeDetector = Window & {
  BarcodeDetector?: BarcodeDetectorCtor;
};

export interface ParsedReceiptItem {
  receiptName: string;
  quantity: number;
  totalPrice: number;
}

export interface ScannerPanelProps {
  mode?: ScannerMode;
  onScan?: (upc: string) => void;
  onCooldown?: (upc: string, reason: 'cooldown' | 'duplicate') => void;
  title?: string;
  subtitle?: string;
  beepEnabled?: boolean;
  cooldownMs?: number;
  onPhotoCaptured?: (photoDataUrl: string, mime: string) => void;
  onReceiptParsed?: (items: ParsedReceiptItem[], frame?: string) => void;
  onModeChange?: (mode: ScannerMode) => void;

  receiptHeaderContent?: React.ReactNode;
  receiptSaveDisabled?: boolean;
  receiptSaveDisabledReason?: string;
  closeOnScan?: boolean;
  manualStart?: boolean;
  /** When false, hide the in-panel Manual UPC fallback (caller owns manual entry). */
  showManualFallback?: boolean;
  onClose?: () => void;
  showClose?: boolean;
  className?: string;
  bottomSheetContent?: React.ReactNode;
  /** When false (default), scans update the status chip but do not auto-expand the sheet over the camera. */
  autoExpandSheetOnScan?: boolean;
  selectedStoreId?: string;
  selectedStoreName?: string;
  selectedStoreBrand?: string;
  selectedStoreLocation?: string;
  selectedStoreIsPrimary?: boolean;
  onTogglePrimarySupplier?: () => void;

  disabled?: boolean;
}

export interface ScannerPanelHandle {
  capturePhoto: () => Promise<void> | void;
}

const ScannerPanel = forwardRef<ScannerPanelHandle, ScannerPanelProps>(({
  mode,
  onScan,
  onCooldown,
  onClose,
  showClose = false,
  title,
  subtitle,
  beepEnabled = true,
  cooldownMs = 1200,
  onPhotoCaptured,
  onReceiptParsed,
  receiptHeaderContent,
  receiptSaveDisabled = false,
  receiptSaveDisabledReason,
  closeOnScan = false,
  manualStart = false,
  showManualFallback = true,
  className = '',
  bottomSheetContent,
  autoExpandSheetOnScan = false,
}, ref) => {
  const core = useCoreContextOptional();
  const addToast = useCallback(
    (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
      core?.addToast?.(message, type);
    },
    [core]
  );
  const { showFlash, flashTone, playBeep, disposeAudio } = useScannerFeedback();
  const { videoRef, streamRef, streamActive, error: cameraError, startCamera, stopCamera } = useCameraStream({ autoStart: false });
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const lastAutoOpenUpcRef = useRef<string | null>(null);

  const rafRef = useRef<number | null>(null);
  const cancelledRef = useRef<boolean>(false);
  const inFlightRef = useRef<boolean>(false);

  const onScanRef = useRef<ScannerPanelProps['onScan']>(onScan);
  const onCloseRef = useRef<ScannerPanelProps['onClose']>(onClose);
  const onCooldownRef = useRef<ScannerPanelProps['onCooldown']>(undefined);
  const closeOnScanRef = useRef<boolean>(closeOnScan);
  const beepEnabledRef = useRef<boolean>(beepEnabled);
  const cooldownMsRef = useRef<number>(getCooldownForMode(mode));

  const lastAcceptAtRef = useRef<number>(0);
  const lastAcceptedCodeRef = useRef<string | null>(null);
  const lastSeenCodeRef = useRef<string | null>(null);
  const stableFramesRef = useRef<number>(0);
  const lastDetectAtRef = useRef<number>(0);
  const zxingReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [manualUpc, setManualUpc] = useState('');
  const [lastDetectedUpc, setLastDetectedUpc] = useState<string | null>(null);
  const [supportsBarcodeDetector, setSupportsBarcodeDetector] = useState(true);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const torchOnRef = useRef(false);

  const isReceiptMode = mode === ScannerMode.RECEIPT_PARSE_LIVE;
  const isManualOnlyFallback = !isReceiptMode && !supportsBarcodeDetector;

  const isMobile =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(pointer:coarse)').matches ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent));

  const useNativeCameraOnly = isReceiptMode && isMobile;

  useEffect(() => {
    torchOnRef.current = torchOn;
  }, [torchOn]);

  const canCapturePhoto = Boolean(onPhotoCaptured);

  useEffect(() => {
    onScanRef.current = onScan;
    onCloseRef.current = onClose;
    onCooldownRef.current = onCooldown;
    closeOnScanRef.current = !!closeOnScan;
    beepEnabledRef.current = beepEnabled;
    cooldownMsRef.current = cooldownMs;
  }, [beepEnabled, closeOnScan, cooldownMs, onClose, onCooldown, onScan]);

  const stopScanner = useCallback(async () => {
    cancelledRef.current = true;
    setIsScanning(false);

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    lastSeenCodeRef.current = null;
    stableFramesRef.current = 0;
    inFlightRef.current = false;

    try {
      if (videoTrackRef.current && torchOnRef.current) {
        await videoTrackRef.current.applyConstraints({ advanced: [{ torch: false } as ExtendedMediaTrackConstraintSet] });
      }
    } catch {
      // ignore torch failures
    }
    setTorchOn(false);

    videoTrackRef.current = null;
    setTorchSupported(false);
    stopCamera();
  }, [stopCamera]);

  const handleClose = useCallback(() => {
    void stopScanner();
    onCloseRef.current?.();
  }, [stopScanner]);

  const acceptScan = useCallback(
    (upc: string) => {
      const now = Date.now();
      const isReturnScan = mode === ScannerMode.CUSTOMER_RETURN_SCAN;
      const cooldownMsValue = isReturnScan ? 450 : cooldownMsRef.current;

      if (now - lastAcceptAtRef.current < cooldownMsValue) {
        return;
      }

      // DO NOT block multiple bottles of the same product for bottle returns.
      // Customers frequently return several identical bottles/cans, and drivers verify at delivery.
      if (!isReturnScan && lastAcceptedCodeRef.current === upc && now - lastAcceptAtRef.current < Math.max(600, cooldownMsValue)) {
        onCooldownRef.current?.(upc, 'duplicate');
        addToast('Same UPC — tap to add again', 'info');
        return;
      }

      lastAcceptAtRef.current = now;
      lastAcceptedCodeRef.current = upc;

      setLastDetectedUpc(upc);
      lastAutoOpenUpcRef.current = upc;
      if (autoExpandSheetOnScan) {
        setIsSheetOpen(true);
      }
      if (beepEnabledRef.current) playBeep();
      onScanRef.current?.(upc);

      if (closeOnScanRef.current && onCloseRef.current) {
        handleClose();
      }
    },
    [autoExpandSheetOnScan, handleClose, playBeep, addToast, mode]
  );

  const startScanner = useCallback(async () => {
    await stopScanner();
    setTorchOn(false);
    cancelledRef.current = false;
    setScannerError(null);

    if (typeof window === 'undefined') {
      setScannerError('Scanner unavailable.');
      return;
    }

    if (useNativeCameraOnly) {
      setIsScanning(false);
      setScannerError(null);
      return;
    }

    if (isReceiptMode) {
      await startCamera({
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      });
      if (cancelledRef.current) {
        stopCamera();
        return;
      }
      if (!streamRef.current) {
        setScannerError('Camera blocked. Enable permissions and retry.');
        setIsScanning(false);
        return;
      }
      const track = streamRef.current.getVideoTracks()[0];
      videoTrackRef.current = track;
      const caps = track?.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      setTorchSupported(Boolean(caps?.torch));
      if (!onReceiptParsed) return;
    }
    const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;
    setSupportsBarcodeDetector(true);

    try {
      const isPortrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
      await startCamera({
        facingMode: 'environment',
        width: { ideal: isPortrait ? 1080 : 1920 },
        height: { ideal: isPortrait ? 1920 : 1080 },
        aspectRatio: { ideal: isPortrait ? 9 / 16 : 16 / 9 },
        frameRate: { ideal: 30 }
      });
      if (cancelledRef.current) {
        stopCamera();
        return;
      }
      if (!streamRef.current || !videoRef.current) {
        setScannerError('Camera failed to start. Tap Retry.');
        setIsScanning(false);
        videoTrackRef.current = null;
        return;
      }

      const track = streamRef.current.getVideoTracks()[0];
      videoTrackRef.current = track;

      const caps = track?.getCapabilities?.() as ExtendedMediaTrackCapabilities | undefined;
      setTorchSupported(Boolean(caps?.torch));

      const BarcodeDetectorConstructor = (window as WindowWithBarcodeDetector).BarcodeDetector;
      const detector = !isReceiptMode && BarcodeDetectorConstructor
        ? new BarcodeDetectorConstructor({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e'] })
        : null;

      if (!isReceiptMode && !detector && !zxingReaderRef.current) {
        zxingReaderRef.current = new BrowserMultiFormatReader();
      }

      const DETECT_MIN_INTERVAL_MS = 100;
      const REQUIRED_STABLE_FRAMES = 1;

      const loop = async () => {
        if (cancelledRef.current) return;
        if (!videoRef.current) return;

        const now = performance.now();
        const interval = isReceiptMode ? 3000 : DETECT_MIN_INTERVAL_MS;
        if (now - lastDetectAtRef.current < interval) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        lastDetectAtRef.current = now;

        if (inFlightRef.current) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }

        if (videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }

        inFlightRef.current = true;
        try {
          // Receipt capture is shutter-driven: frames are not sent to OCR automatically in loop
          if (!isReceiptMode) {
            let detectedRawValue: string | null = null;

            if (detector) {
              const barcodes = await detector.detect(videoRef.current);
              if (cancelledRef.current) return;
              if (barcodes && barcodes.length > 0) {
                detectedRawValue = String(barcodes[0]?.rawValue || '');
              }
            } else if (zxingReaderRef.current && videoRef.current) {
              const video = videoRef.current;
              if (!canvasRef.current) {
                canvasRef.current = document.createElement('canvas');
              }
              const canvas = canvasRef.current;
              if (video.videoWidth && video.videoHeight) {
                canvas.width = Math.min(640, video.videoWidth);
                canvas.height = Math.floor(canvas.width * (video.videoHeight / video.videoWidth));
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx) {
                  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                  try {
                    const result = zxingReaderRef.current.decodeFromCanvas(canvas);
                    if (result) {
                      detectedRawValue = result.getText();
                    }
                  } catch {
                    // No barcode in frame
                  }
                }
              }
            }

            if (!detectedRawValue) {
              lastSeenCodeRef.current = null;
              stableFramesRef.current = 0;
              return;
            }

            const { ok, upc } = validateUpc(detectedRawValue);
            if (!ok) return;

            if (lastSeenCodeRef.current === upc) {
              stableFramesRef.current += 1;
            } else {
              lastSeenCodeRef.current = upc;
              stableFramesRef.current = 1;
            }

            if (stableFramesRef.current >= REQUIRED_STABLE_FRAMES) {
              lastSeenCodeRef.current = null;
              stableFramesRef.current = 0;
              acceptScan(upc);
            }
          }
        } catch {
          // ignore detection errors
        } finally {
          inFlightRef.current = false;
          if (!cancelledRef.current) {
            rafRef.current = requestAnimationFrame(loop);
          }
        }
      };

      rafRef.current = requestAnimationFrame(loop);
    } catch {
      setScannerError('Camera blocked. Enable permissions.');
      setIsScanning(false);
    }
  }, [acceptScan, stopScanner, isReceiptMode, onReceiptParsed, useNativeCameraOnly, startCamera, stopCamera, streamRef, videoRef]);

  const captureReceiptAndParse = useCallback(async () => {
    if (!videoRef.current) {
      addToast('Camera not ready for capture', 'error');
      return;
    }

    void stopScanner();

    try {
      const dataUrl = await compressAndResizeImage(
        videoRef.current,
        MAX_IMAGE_DIMENSION,
        MAX_IMAGE_DIMENSION,
        IMAGE_COMPRESSION_QUALITY
      );
      if (!dataUrl || !dataUrl.startsWith('data:image/')) {
        throw new Error('Invalid captured image');
      }

      if (onPhotoCaptured) {
        onPhotoCaptured(dataUrl, 'image/jpeg');
        addToast('Receipt photo captured', 'success');
        return;
      }

      if (onReceiptParsed) {
        onReceiptParsed([], dataUrl);
      }

      setPreviewImage(dataUrl);
      addToast('Receipt photo ready for review', 'info');
    } catch {
      setScannerError('Failed to capture image.');
      addToast('Failed to capture image', 'error');
      void startScanner();
    }
  }, [stopScanner, startScanner, addToast, videoRef, onReceiptParsed, onPhotoCaptured]);

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !onPhotoCaptured) {
      addToast('Camera not ready for photo', 'error');
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      addToast('Failed to access camera', 'error');
      return;
    }

    const w = videoRef.current.videoWidth;
    const h = videoRef.current.videoHeight;
    if (!w || !h) {
      addToast('Camera not ready for photo', 'error');
      return;
    }

    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(videoRef.current, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    addToast('Photo captured', 'success');
    onPhotoCaptured(dataUrl, 'image/jpeg');
  }, [onPhotoCaptured, addToast, videoRef]);

  useImperativeHandle(ref, () => ({
    capturePhoto: () => {
      if (mode === ScannerMode.RECEIPT_PARSE_LIVE) {
        return captureReceiptAndParse();
      }
      return takePhoto();
    }
  }));

  const toggleTorch = useCallback(async () => {
    if (!torchSupported) {
      setScannerError('Torch not supported on this device.');
      addToast('Torch not supported', 'error');
      return;
    }
    if (!videoTrackRef.current) return;
    const next = !torchOnRef.current;
    try {
      await videoTrackRef.current.applyConstraints({ advanced: [{ torch: next } as ExtendedMediaTrackConstraintSet] });
      setTorchOn(next);
      addToast(next ? 'Torch enabled' : 'Torch disabled', 'info');
    } catch {
      setScannerError('Failed to toggle torch. Your device or browser may not support this feature.');
      addToast('Failed to toggle torch', 'error');
    }
  }, [torchSupported, addToast]);

  useEffect(() => {
    setIsScanning(streamActive);
  }, [streamActive]);

  useEffect(() => {
    if (cameraError && !scannerError) {
      setScannerError('Camera blocked. Enable permissions.');
    }
  }, [cameraError, scannerError]);

  const handleReceiptFile = useCallback(
    (file: File) => {
      if (!file) {
        addToast('No file selected', 'error');
        return;
      }
      if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        setScannerError(`File too large. Max size is ${MAX_UPLOAD_MB}MB.`);
        addToast('File too large', 'error');
        return;
      }
      if (file.type === 'application/pdf') {
        setScannerError('PDF receipts are not supported. Please upload an image (JPEG, PNG, or HEIC).');
        addToast('Image required (JPEG/PNG/HEIC)', 'info');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setScannerError('Unsupported file type. Please upload an image.');
        addToast('Unsupported file type', 'error');
        return;
      }
      void stopScanner();
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = async () => {
          try {
            const dataUrl = await compressAndResizeImage(
              img,
              MAX_IMAGE_DIMENSION,
              MAX_IMAGE_DIMENSION,
              IMAGE_COMPRESSION_QUALITY
            );
            if (!dataUrl || !dataUrl.startsWith('data:image/')) {
              throw new Error('Invalid captured image');
            }
            setPreviewImage(dataUrl);
            addToast('Image ready for review', 'info');
          } catch {
            setScannerError('Failed to process image.');
            addToast('Failed to process image', 'error');
            void startScanner();
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    },
    [startScanner, stopScanner, addToast]
  );

  const handleReceiptFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleReceiptFile(file);
      }
      event.target.value = '';
    },
    [handleReceiptFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragActive(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleReceiptFile(file);
      }
    },
    [handleReceiptFile]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleManualScan = useCallback(() => {
    const { ok, upc } = validateUpc(manualUpc);
    if (!ok) {
      addToast('Invalid UPC', 'error');
      return;
    }
    addToast('Manual UPC submitted', 'success');
    acceptScan(upc);
    setManualUpc('');
  }, [acceptScan, manualUpc, addToast]);

  const handleToggleSheet = useCallback(() => {
    setIsSheetOpen(prev => !prev);
  }, []);

  useEffect(() => {
    return () => {
      void stopScanner();
      disposeAudio();
    };
  }, [stopScanner, disposeAudio]);

  useEffect(() => {
    if (!lastDetectedUpc) return;
    if (lastAutoOpenUpcRef.current === lastDetectedUpc) return;
    lastAutoOpenUpcRef.current = lastDetectedUpc;
    setIsSheetOpen(true);
  }, [lastDetectedUpc]);

  useEffect(() => {
    if (manualStart) {
      void stopScanner();
      setScannerError(null);
      setIsScanning(false);
      return;
    }

    let active = true;
    const restartScannerForMode = async () => {
      await stopScanner();
      if (!active) return;
      await startScanner();
    };

    void restartScannerForMode();

    return () => {
      active = false;
    };
  }, [manualStart, mode, startScanner, stopScanner]);

  useEffect(() => {
    if (scannerError) {
      addToast(scannerError, 'error');
    }
  }, [scannerError, addToast]);

  return (
    <div className={`relative flex flex-col w-full h-full overflow-hidden bg-black ${className || ''}`}>
      <ScannerFlashOverlay show={showFlash} tone={flashTone} />

      {!useNativeCameraOnly && (
        <div className="absolute inset-0 flex items-center justify-center">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
          <ScannerLaserOverlay
            active={isScanning}
            instruction={isReceiptMode ? "Align receipt inside frame" : "Align barcode inside frame"}
          />
          {!isScanning && (
            <ScannerCameraIdleOverlay
              scannerError={scannerError}
              manualStart={manualStart}
              onRetry={() => void startScanner()}
            />
          )}
        </div>
      )}

      <div className="relative z-10 flex flex-col gap-2 p-4 pt-safe">
        <div className="flex items-center justify-between">
          {showClose && onClose ? (
            <button
              onClick={handleClose}
              aria-label="Close scanner"
              className="w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white border border-white/20 backdrop-blur-md flex items-center justify-center transition active:scale-95"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          ) : (
            <div className="w-10 h-10" />
          )}

          <div className="flex flex-col items-center pointer-events-none px-2 text-center">
            <span className="text-xs font-black uppercase tracking-wider text-white drop-shadow-md">
              {title || (isReceiptMode ? 'Receipt Scanner' : 'Barcode Scanner')}
            </span>
            {subtitle && (
              <span className="text-[10px] text-slate-300 font-semibold drop-shadow-sm line-clamp-1">
                {subtitle}
              </span>
            )}
          </div>

          <button
            onClick={() => void toggleTorch()}
            disabled={!torchSupported}
            className={`w-10 h-10 rounded-full border backdrop-blur-md transition flex items-center justify-center active:scale-95 ${
              torchSupported
                ? torchOn
                  ? 'bg-yellow-400 text-black border-yellow-300 shadow-[0_0_12px_rgba(250,204,21,0.6)]'
                  : 'bg-black/60 text-white border-white/20 hover:bg-black/80'
                : 'bg-black/40 text-gray-500 border-white/10 opacity-50 cursor-not-allowed'
            }`}
            title={torchSupported ? (torchOn ? 'Turn off torch' : 'Turn on torch') : 'Torch not supported'}
          >
            {torchOn ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {isReceiptMode && receiptHeaderContent && (
        <ScannerReceiptHeader>{receiptHeaderContent}</ScannerReceiptHeader>
      )}

      {isReceiptMode && canCapturePhoto && !previewImage && (
        <ScannerReceiptControls
          useNativeCameraOnly={useNativeCameraOnly}
          receiptSaveDisabled={receiptSaveDisabled}
          receiptSaveDisabledReason={receiptSaveDisabledReason}
          isDragActive={isDragActive}
          onCapture={() => void captureReceiptAndParse()}
          onFileChange={handleReceiptFileInput}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        />
      )}

      {bottomSheetContent && (
        <div
          className={`relative z-10 mt-auto w-full overflow-hidden rounded-t-[2rem] bg-ninpo-black/95 backdrop-blur-xl border-t border-white/10 shadow-2xl transition-all duration-300 ease-out ${
            isSheetOpen ? 'max-h-[70vh]' : 'max-h-[96px]'
          }`}
        >
          <div className="sticky top-0 bg-ninpo-black/95 backdrop-blur-xl z-10 px-6 pt-4 pb-3">
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                onClick={handleToggleSheet}
                className="flex items-center gap-2 rounded-full px-3 py-2 bg-white/5 border border-white/10 text-white/80 text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition"
                aria-expanded={isSheetOpen}
              >
                <span className="h-1 w-10 rounded-full bg-white/30" />
                {isSheetOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                <span>{isSheetOpen ? 'Close' : 'Expand'}</span>
              </button>

              <div className="flex flex-col items-end">
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{title}</p>
                <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">{subtitle}</p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                <ScanLine className="w-4 h-4 text-ninpo-lime" />
                <span>{lastDetectedUpc ? 'Latest scan' : 'Waiting for scan'}</span>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Scanned</p>
                <p className="text-sm text-ninpo-lime font-black">{lastDetectedUpc ?? '—'}</p>
              </div>
            </div>
          </div>

          {isSheetOpen && (
            <div className="px-6 pb-6 space-y-4 overflow-y-auto max-h-[60vh]">
              {lastDetectedUpc ? (
                bottomSheetContent
              ) : (
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest">
                  Waiting for a scan to auto-fill.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {scannerError && <ScannerErrorChip message={scannerError} />}

      {!isReceiptMode && showManualFallback && isManualOnlyFallback && (
        <ScannerManualUpcFallback
          manualUpc={manualUpc}
          onManualUpcChange={setManualUpc}
          onSubmit={handleManualScan}
        />
      )}
    </div>
  );
});

export default ScannerPanel;
